import { Component } from "@theme/component";

/**
 * Header Quick Add Component
 * A clean, focused component that opens a custom quick add modal for header products.
 * Uses only theme settings and product data without fetching product page content.
 * Supports multiple trigger buttons/links across the site.
 *
 * @extends {Component}
 */
export class HeaderQuickAddComponent extends Component {
    get productId() {
        return this.dataset.productId;
    }

    get productTitle() {
        return this.dataset.productTitle;
    }

    get productUrl() {
        return this.dataset.productUrl;
    }

    get showPaymentButtons() {
        return this.dataset.showPaymentButtons === "true";
    }

    get colorScheme() {
        return this.dataset.colorScheme;
    }

    get inheritColorScheme() {
        return this.dataset.inheritColorScheme === "true";
    }

    get heading() {
        return this.dataset.heading;
    }

    get productDescription() {
        return this.dataset.productDescription;
    }

    get showQuantitySelector() {
        return this.dataset.showQuantitySelector === "true";
    }

    get addToCartText() {
        return this.dataset.addToCartText;
    }

    get additionalImage() {
        return this.dataset.additionalImage;
    }

    connectedCallback() {
        super.connectedCallback();
        this.addEventListener("click", this.handleClick);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.removeEventListener("click", this.handleClick);
    }

    /**
     * Handles quick add button click
     * @param {Event} event - The click event
     */
    handleClick = (event) => {
        event.preventDefault();
        this.openHeaderQuickAddModal();
    };

    /**
     * Opens the header quick add modal
     */
    openHeaderQuickAddModal = () => {
        const dialogComponent = document.getElementById(
            "header-quick-add-dialog"
        );

        if (!dialogComponent) {
            console.warn(
                "Header quick add dialog not found. Make sure the header-quick-add-modal snippet is included in your theme.liquid"
            );
            return;
        }

        // Apply color scheme to the modal if specified
        if (!this.inheritColorScheme && this.colorScheme) {
            const innerDialog = dialogComponent.querySelector("dialog");
            if (innerDialog) {
                // Remove existing color scheme classes
                innerDialog.classList.remove(
                    "color-scheme-1",
                    "color-scheme-2",
                    "color-scheme-3",
                    "color-scheme-4",
                    "color-scheme-5"
                );
                // Add the new color scheme class
                innerDialog.classList.add(`color-${this.colorScheme}`);
            }
        }

        // Open the modal
        const dialog = dialogComponent.querySelector("dialog");
        if (dialog) {
            dialog.showModal();

            // Set up close functionality
            this.setupModalCloseHandlers(dialog);
        }
    };

    /**
     * Sets up modal close handlers for close button and clicking outside
     * @param {HTMLDialogElement} dialog - The dialog element
     */
    setupModalCloseHandlers = (dialog) => {
        // Close button handler
        const closeButton = dialog.querySelector(
            ".header-quick-add-modal__close"
        );
        if (closeButton) {
            closeButton.addEventListener("click", () => {
                dialog.close();
            });
        }

        // Click outside modal to close
        dialog.addEventListener("click", (event) => {
            if (event.target === dialog) {
                dialog.close();
            }
        });

        // Close on Escape key
        dialog.addEventListener("keydown", (event) => {
            if (event.key === "Escape") {
                dialog.close();
            }
        });
    };
}

// Register the custom element
customElements.define("header-quick-add-component", HeaderQuickAddComponent);

/**
 * Global Header Quick Add Manager
 * Handles multiple trigger buttons/links across the site
 */
class HeaderQuickAddManager {
    constructor() {
        this.init();
    }

    init() {
        // Handle clicks on elements with data-header-quick-add attribute
        document.addEventListener("click", (event) => {
            const trigger = event.target.closest("[data-header-quick-add]");
            if (trigger) {
                event.preventDefault();
                this.openModalFromTrigger(trigger);
            }
        });

        // Handle clicks on elements with href="#header-quick-add"
        document.addEventListener("click", (event) => {
            const link = event.target.closest('a[href="#header-quick-add"]');
            if (link) {
                event.preventDefault();
                this.openModalFromLink(link);
            }
        });
    }

    /**
     * Opens modal from a trigger element with data attributes
     * @param {HTMLElement} trigger - The trigger element
     */
    openModalFromTrigger(trigger) {
        const dialogComponent = document.getElementById(
            "header-quick-add-dialog"
        );
        if (!dialogComponent) {
            console.warn("Header quick add dialog not found");
            return;
        }

        // Get settings from the trigger element
        const settings = this.getSettingsFromTrigger(trigger);

        // Apply settings to the modal
        this.applySettingsToModal(dialogComponent, settings);

        // Open the modal
        const dialog = dialogComponent.querySelector("dialog");
        if (dialog) {
            dialog.showModal();
            this.setupModalCloseHandlers(dialog);
        }
    }

    /**
     * Opens modal from a link with href="#header-quick-add"
     * @param {HTMLElement} link - The link element
     */
    openModalFromLink(link) {
        const dialogComponent = document.getElementById(
            "header-quick-add-dialog"
        );
        if (!dialogComponent) {
            console.warn("Header quick add dialog not found");
            return;
        }

        // Get settings from the link's data attributes or use defaults
        const settings = this.getSettingsFromLink(link);

        // Apply settings to the modal
        this.applySettingsToModal(dialogComponent, settings);

        // Open the modal
        const dialog = dialogComponent.querySelector("dialog");
        if (dialog) {
            dialog.showModal();
            this.setupModalCloseHandlers(dialog);
        }
    }

    /**
     * Gets settings from a trigger element
     * @param {HTMLElement} trigger - The trigger element
     * @returns {Object} Settings object
     */
    getSettingsFromTrigger(trigger) {
        return {
            productId: trigger.dataset.productId || "",
            productTitle: trigger.dataset.productTitle || "",
            productUrl: trigger.dataset.productUrl || "",
            showPaymentButtons: trigger.dataset.showPaymentButtons === "true",
            colorScheme: trigger.dataset.colorScheme || "",
            inheritColorScheme: trigger.dataset.inheritColorScheme === "true",
            heading: trigger.dataset.heading || "",
            productDescription: trigger.dataset.productDescription || "",
            showQuantitySelector:
                trigger.dataset.showQuantitySelector === "true",
            addToCartText: trigger.dataset.addToCartText || "",
            additionalImage: trigger.dataset.additionalImage || "",
        };
    }

    /**
     * Gets settings from a link element
     * @param {HTMLElement} link - The link element
     * @returns {Object} Settings object
     */
    getSettingsFromLink(link) {
        // Use data attributes if available, otherwise use theme defaults
        return {
            productId: link.dataset.productId || "",
            productTitle: link.dataset.productTitle || "",
            productUrl: link.dataset.productUrl || "",
            showPaymentButtons: link.dataset.showPaymentButtons === "true",
            colorScheme: link.dataset.colorScheme || "",
            inheritColorScheme: link.dataset.inheritColorScheme === "true",
            heading: link.dataset.heading || "",
            productDescription: link.dataset.productDescription || "",
            showQuantitySelector: link.dataset.showQuantitySelector === "true",
            addToCartText: link.dataset.addToCartText || "",
            additionalImage: link.dataset.additionalImage || "",
        };
    }

    /**
     * Applies settings to the modal
     * @param {HTMLElement} dialogComponent - The dialog component
     * @param {Object} settings - The settings to apply
     */
    applySettingsToModal(dialogComponent, settings) {
        const dialog = dialogComponent.querySelector("dialog");
        if (!dialog) return;

        // Apply color scheme
        if (!settings.inheritColorScheme && settings.colorScheme) {
            dialog.classList.remove(
                "color-scheme-1",
                "color-scheme-2",
                "color-scheme-3",
                "color-scheme-4",
                "color-scheme-5"
            );
            dialog.classList.add(`color-${settings.colorScheme}`);
        }

        // Update modal content if custom settings are provided
        if (settings.heading) {
            const header = dialog.querySelector(
                ".header-quick-add-modal__header"
            );
            if (header) {
                header.innerHTML = settings.heading;
            }
        }

        if (settings.productDescription) {
            const description = dialog.querySelector(".product-description");
            if (description) {
                description.innerHTML = settings.productDescription;
            }
        }

        if (settings.addToCartText) {
            const button = dialog.querySelector(
                ".add-to-cart-button .button__text"
            );
            if (button) {
                button.textContent = settings.addToCartText;
            }
        }
    }

    /**
     * Sets up modal close handlers
     * @param {HTMLDialogElement} dialog - The dialog element
     */
    setupModalCloseHandlers(dialog) {
        // Close button handler
        const closeButton = dialog.querySelector(
            ".header-quick-add-modal__close"
        );
        if (closeButton) {
            closeButton.addEventListener("click", () => {
                dialog.close();
            });
        }

        // Click outside modal to close
        dialog.addEventListener("click", (event) => {
            if (event.target === dialog) {
                dialog.close();
            }
        });

        // Close on Escape key
        dialog.addEventListener("keydown", (event) => {
            if (event.key === "Escape") {
                dialog.close();
            }
        });
    }
}

// Initialize the global manager when the DOM is ready
if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
        new HeaderQuickAddManager();
    });
} else {
    new HeaderQuickAddManager();
}
